import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { TStore, useAppDispatch } from '../store';
import { getStrainData } from '../actions/strainAction';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Spin } from 'antd';
import { StrainAnalyzer, StrainReading, StrainMetrics } from '../utils/strainAnalyzer';

const Strain: React.FC = () => {
  const dispatch = useAppDispatch();
  const { strainData, loading, error } = useSelector((state: TStore) => state.strain);
  const navigate = useNavigate();
  
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('7d');
  const [viewMode, setViewMode] = useState<'chart' | 'zones'>('chart');

  const selectedVessel: any = useSelector((state: TStore) => {
    const vessel = state.averageFleetDetails?.selectedVessel;
    if (vessel?.shipId) {
      console.log('Found vessel in averageFleetDetails:', vessel);
      return vessel;
    }
    return null;
  });

  // Fetch strain data on component mount and when vessel changes
  useEffect(() => {
    console.log('Fetching strain data with shipId:', selectedVessel?.shipId);
    
    if (selectedVessel?.shipId) {
      dispatch(getStrainData(selectedVessel.shipId));
    } else {
      // Fetch all data if no vessel selected
      dispatch(getStrainData(1));
    }
  }, [dispatch, selectedVessel?.shipId]);

  // STEP 1: Filter data by selected vessel first (if applicable)
  const vesselFilteredData = React.useMemo(() => {
    if (!strainData || strainData.length === 0) return [];
    
    let vesselData = strainData;
    if (selectedVessel?.shipId) {
      vesselData = strainData.filter((item: StrainReading) => item.shipId === selectedVessel.shipId);
    }
    
    console.log('Vessel filtered data length:', vesselData.length);
    console.log('Sample vessel data:', vesselData.slice(0, 3));
    
    return vesselData;
  }, [strainData, selectedVessel?.shipId]);

  // STEP 2: Aggregate data by date (multiple users per date become one record per date)
  const aggregatedData = React.useMemo(() => {
    const aggregated = StrainAnalyzer.aggregateDataByDate(vesselFilteredData);
    console.log('Aggregated data length:', aggregated.length);
    console.log('Sample aggregated data:', aggregated.slice(0, 3));
    return aggregated;
  }, [vesselFilteredData]);

  // STEP 3: Filter by time range (7d, 30d, 90d)
  const filteredData = React.useMemo(() => {
    const timeFiltered = StrainAnalyzer.filterDataByTimeRange(aggregatedData, timeRange);
    console.log(`Time filtered data (${timeRange}):`, timeFiltered.length);
    console.log('Date range:', {
      from: timeFiltered[0]?.date,
      to: timeFiltered[timeFiltered.length - 1]?.date
    });
    return timeFiltered;
  }, [aggregatedData, timeRange]);

  // Calculate metrics from real data
  const metrics: StrainMetrics = React.useMemo(() => {
    if (filteredData.length === 0) {
      return {
        current: 0,
        average: 0,
        peak: 0,
        trend: 'stable',
        weeklyAverage: 0,
        averageHeartRate: 0,
        totalWorkouts: 0,
        workoutStrainPercentage: 0
      };
    }

    const weeklyData = filteredData.slice(-7);
    
    return {
      current: selectedVessel?.averageStrain ?? filteredData[filteredData.length - 1]?.strain ?? 0,
      average: filteredData.reduce((a, b) => a + b.strain, 0) / filteredData.length,
      peak: Math.max(...filteredData.map(d => d.strain)),
      trend: StrainAnalyzer.calculateTrend(filteredData.map(d => d.strain), timeRange),
      weeklyAverage: weeklyData.reduce((a, b) => a + b.strain, 0) / weeklyData.length,
      averageHeartRate: filteredData.reduce((a, b) => a + b.averageHeartRate, 0) / filteredData.length,
      totalWorkouts: filteredData.reduce((a, b) => a + b.totalWorkouts, 0),
      workoutStrainPercentage: filteredData.reduce((a, b) => a + b.percentageOfDailyStrainFromWorkout, 0) / filteredData.length
    };
  }, [filteredData, selectedVessel?.averageStrain]);

  const currentZone = StrainAnalyzer.determineZone(metrics.current);

  const zoneDistribution = React.useMemo(() => {
    return StrainAnalyzer.getStrainZones().map(zone => ({
      ...zone,
      count: filteredData.filter(d => d.strain >= zone.min && d.strain <= zone.max).length,
      percentage: filteredData.length > 0 ? 
        (filteredData.filter(d => d.strain >= zone.min && d.strain <= zone.max).length / filteredData.length) * 100 : 0
    }));
  }, [filteredData]);

  // Prepare chart data
  const chartData = filteredData.map(item => ({
    ...item,
    date: new Date(item.date).toISOString().split('T')[0],
    sports: item.sports || []
  }));

  if (loading) {
    return (
      <div className="strain-analysis">
        <Spin className='recovery-analysis__loading' />
      </div>
    );
  }

  if (error) {
    return (
      <div className="strain-analysis">
        <div className="strain-analysis__error">Error loading strain data: {error}</div>
      </div>
    );
  }

  if (filteredData.length === 0) {
    return (
      <div className="strain-analysis">
        <div className="strain-analysis__no-data">
          No strain data available for the selected time range and vessel.
        </div>
      </div>
    );
  }

  return (
    <div className="strain-analysis">
      {/* Header */}
      <div className="strain-analysis__header">
        <div className="strain-analysis__title-section">
          <h1 className="strain-analysis__title">Strain Analysis</h1>
          <p className="strain-analysis__subtitle">
            Cardiovascular load and intensity tracking
          </p>
        </div>
        
        <div className="strain-analysis__controls">
          <div className="control-group" role="group" aria-label="Time range">
            <button
              className={`control-btn ${timeRange === '7d' ? 'control-btn--active' : ''}`}
              onClick={() => setTimeRange('7d')}
              aria-pressed={timeRange === '7d'}
            >
              7D
            </button>
            <button
              className={`control-btn ${timeRange === '30d' ? 'control-btn--active' : ''}`}
              onClick={() => setTimeRange('30d')}
              aria-pressed={timeRange === '30d'}
            >
              30D
            </button>
            <button
              className={`control-btn ${timeRange === '90d' ? 'control-btn--active' : ''}`}
              onClick={() => setTimeRange('90d')}
              aria-pressed={timeRange === '90d'}
            >
              90D
            </button>
          </div>

          <div className="control-group" role="group" aria-label="View mode">
            <button
              className={`control-btn ${viewMode === 'chart' ? 'control-btn--active' : ''}`}
              onClick={() => setViewMode('chart')}
              aria-pressed={viewMode === 'chart'}
            >
              Chart
            </button>
            <button
              className={`control-btn ${viewMode === 'zones' ? 'control-btn--active' : ''}`}
              onClick={() => setViewMode('zones')}
              aria-pressed={viewMode === 'zones'}
            >
              Zones
            </button>
          </div>
          <button className="btn-back" onClick={() => navigate(-1)}>
            <span className="btn-back__arrow" aria-hidden="true">←</span>
            Back to Dashboard
          </button>
        </div>
      </div>

      {/* Current Strain Display */}
      <div className="strain-analysis__current">
        <div className="current-strain">
          <div className="current-strain__value" style={{ color: currentZone.color }}>
            {metrics.average.toFixed(1)}
          </div>
          <div className="current-strain__zone">
            <span className="zone-indicator" style={{ backgroundColor: currentZone.color }}></span>
            {currentZone.label} Strain
          </div>
          <div className="current-strain__description">
            {currentZone.description}
          </div>
        </div>
        
        <div className="strain-metrics">
          <div className="metric-card">
            <div className="metric-card__value">{metrics.average.toFixed(1)}</div>
            <div className="metric-card__label">Avg Strain ({timeRange})</div>
          </div>
          <div className="metric-card">
            <div className="metric-card__value">{metrics.peak.toFixed(1)}</div>
            <div className="metric-card__label">Peak</div>
          </div>
          <div className="metric-card">
            <div className="metric-card__value">{Math.round(metrics.averageHeartRate)}</div>
            <div className="metric-card__label">Avg HR</div>
          </div>
          <div className="metric-card">
            <div className="metric-card__value">
              <span className={`trend-indicator trend-indicator--${metrics.trend}`}>
                {metrics.trend === 'up' ? '↗' : metrics.trend === 'down' ? '↘' : '→'}
              </span>
            </div>
            <div className="metric-card__label">Trend</div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="strain-analysis__content">
        {viewMode === 'chart' ? (
          <div className="chart-container">
            <h3 className="chart-container__title">Strain Over Time</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis 
                  domain={[0, 21]} 
                  tick={{ fontSize: 12 }}
                  label={{ value: 'Strain', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip 
                  formatter={(value: any) => [value.toFixed(1), 'Strain']}
                  labelFormatter={(label) => new Date(label).toLocaleDateString()}
                />
                <Line 
                  type="monotone" 
                  dataKey="strain" 
                  stroke="#FF6B6B" 
                  strokeWidth={3}
                  dot={{ fill: '#FF6B6B', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: '#FF6B6B', strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="zones-container">
            <h3 className="zones-container__title">Strain Zone Distribution</h3>
            <div className="zone-bars">
              {zoneDistribution.map((zone, index) => (
                <div key={index} className="zone-bar">
                  <div className="zone-bar__info">
                    <span className="zone-bar__name" style={{ color: zone.color }}>
                      {zone.label}
                    </span>
                    <span className="zone-bar__range">
                      {zone.min}-{zone.max}
                    </span>
                  </div>
                  <div className="zone-bar__progress">
                    <div 
                      className="zone-bar__fill" 
                      style={{ 
                        width: `${zone.percentage}%`, 
                        backgroundColor: zone.color 
                      }}
                    ></div>
                  </div>
                  <div className="zone-bar__stats">
                    <span className="zone-bar__count">{zone.count} days</span>
                    <span className="zone-bar__percentage">{zone.percentage.toFixed(0)}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Strain;